import * as React from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridRowsProp,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarExport,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import DataGridCompat from "../../components/global/DataGridCompat";
import Link from "@mui/material/Link";
import AddIcon from "@mui/icons-material/Add";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import NoResultsOverlay from "../../components/NoResultOverlay";
import useDebounce from "../../hooks/useDebounce";
// import { Talent } from "../../types/users";
import {
  NegotiationStatus,
  Project,
  ProjectResource,
  ProjectStatus,
} from "../../types/project";
import { useNavigate } from "react-router-dom";
import { useGetProjectsQuery } from "../../services/project.service";
import {
  Box,
  Chip,
  FormHelperText,
  Icon,
  IconButton,
  Typography,
} from "@mui/material";
import { Theme, useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import dayjs from "dayjs";
import { OpenInNew, Person, Place, Refresh, Sort } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useCompany } from "../../hooks/useCompany";
import { useUILabel } from "../../hooks/useUILabel";
import { CompanyStatus } from "../../types/company";
import { getHiredTalentStatus } from "../../utils/projectHelper";
import ModalUploadDocuments from "./ModalUploadDocuments";
import { formatWithTimezone } from "../../utils/dateHelper";
//import  CreateTransform  from "redux-persist/lib/createTransform";

const TableToolbar = ({
  onCreate,
  onRefresh,
  onSearch,
  onShowMap,
}: {
  onSearch: (query?: string) => void;
  onRefresh: () => void;
  onCreate: () => void;
  onShowMap: () => void;
}) => {
  const company = useCompany();
  const enterToSearchLabel = useUILabel("common.enterToSearch");
  const resetLabel = useUILabel("common.reset");
  const showMapLabel = useUILabel("project.showMap");
  const createNewWorkLabel = useUILabel("project.createNewWork");
  const refreshLabel = useUILabel("common.refresh");
  const [showUploadModal, setShowUploadModal] = React.useState<boolean>(false);
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);
  React.useEffect(() => {
    onSearch(debounceQuery);
  }, [debounceQuery]);

  return (
    <GridToolbarContainer
      sx={{
        padding: "16px 20px",
        marginBottom: 0,
        backgroundColor: "transparent",
        borderBottom: (theme) =>
          `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
      }}
    >
      <TextField
        margin="dense"
        label={enterToSearchLabel}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{
          minWidth: 250,
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(255, 255, 255, 0.9)",
            },
          },
        }}
      />
      {!!query && (
        <Button
          variant="text"
          sx={{
            mt: 1,
            ml: 1,
            borderRadius: "8px",
            textTransform: "none",
          }}
          onClick={() => setQuery("")}
        >
          {resetLabel}
        </Button>
      )}
      <Stack
        sx={{ flex: 1 }}
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        flexWrap="wrap"
      >
        <Box sx={{ mr: 1 }}>
          <GridToolbarExport
            printOptions={{ disableToolbarButton: true }}
            csvOptions={{ allColumns: true, fileName: "Jobs" }}
          />
        </Box>
        <Button
          variant="contained"
          onClick={onShowMap}
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <LocationOnIcon sx={{ mr: 0.5 }} /> {showMapLabel}
        </Button>
        <Button
          variant="contained"
          onClick={onCreate}
          disabled={company?.status != CompanyStatus.ACTIVE}
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <AddIcon sx={{ mr: 0.5 }} /> {createNewWorkLabel}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            setShowUploadModal(true);
          }}
          disabled={company?.status != CompanyStatus.ACTIVE}
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <AddIcon sx={{ mr: 0.5 }} /> Upload Documents
        </Button>
        <LoadingButton
          size="large"
          onClick={onRefresh}
          endIcon={<Refresh />}
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {refreshLabel}
        </LoadingButton>
      </Stack>
      <ModalUploadDocuments
        open={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
        }}
      />
    </GridToolbarContainer>
  );
};

interface ProjectGridProps {
  onView: (item: any) => void;
  status: ProjectStatus;
  filter?: "scheduled" | "inProgress";
}

const ProjectGrid = (props: ProjectGridProps) => {
  const { onView, status, filter } = props;
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const locationLabel = useUILabel("common.location");
  const managerLabel = useUILabel("manager.manager");
  const workIdLabel = useUILabel("project.workId");
  const workNameLabel = useUILabel("project.workName");
  const workIdWorkTitleLabel = useUILabel("project.workIdWorkTitle");
  const privateLabel = useUILabel("project.private");
  const startAtLabel = useUILabel("project.startAt");
  const offersLabel = useUILabel("project.offers");
  const invitesLabel = useUILabel("project.invites");

  const navigate = useNavigate();

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const { data, isLoading, isFetching, refetch } = useGetProjectsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : "start_date",
    sortMode: sortModel.length > 0 ? sortModel[0].sort : "desc",
    search: searchQuery,
    status,
  });
  const rows: GridRowsProp | undefined = React.useMemo(() => {
    if (filter) {
      const newData = data?.data.filter((item) => {
        const hiredList = item.resources?.filter((it) => !!it.hired_at) || [];
        let isInProgress = false;
        hiredList.map((hiredTalent) => {
          if (
            hiredTalent.report_progresses?.length != undefined &&
            hiredTalent.report_progresses?.length > 0
          )
            isInProgress = true;
        });
        if (filter === "inProgress") return !!isInProgress;
        if (filter === "scheduled") return !isInProgress;
      });
      return newData;
    } else {
      return data?.data;
    }
  }, [data]);

  const rowCount = React.useMemo(() => {
    return data?.meta.total;
  }, [data]);

  const onBidClick = (row: Project) => {
    navigate(`/works/${row?.id}?t=1`);
  };

  const columns: GridColDef[] = React.useMemo(() => {
    const col: GridColDef[] = [];
    col.push(
      {
        field: "id",
        headerName: workIdLabel,
        minWidth: isMobile ? 80 : undefined,
        width: isMobile ? 80 : undefined,
      },
      {
        field: "title",
        headerName: workNameLabel,
        minWidth: isMobile ? 120 : undefined,
        width: isMobile ? 120 : undefined,
      },
      {
        field: "client_location",
        headerName: locationLabel,
        headerAlign: "center",
        minWidth: isMobile ? 120 : 200,
        width: isMobile ? 120 : undefined,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params?.row) return "-";
          if (params.row.location?.name) {
            return params.row.location.name;
          }
          return "-";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params?.row?.location?.name) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography variant="body2">
              {params.row.location.name}
            </Typography>
          );
        },
      },
      {
        field: "job_title",
        headerName: workIdWorkTitleLabel,
        flex: 1,
        minWidth: isMobile ? 200 : 300,
        sortable: true,
        filterable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Box key={params.row.id}>
            <Stack direction={"row"} alignItems="center" spacing={1}>
              <Link
                component="button"
                variant="body2"
                onClick={() => onView(params.row)}
                sx={{
                  color: "#6eb678",
                  fontWeight: 600,
                  fontSize: isMobile ? "0.8rem" : "0.95rem",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "#5a9f65",
                  },
                }}
              >
                #{params.row.id} - {params.row.title}
              </Link>
              {!!params.row.is_private && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 0.5,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  (Private)
                </Typography>
              )}
            </Stack>
            {!!params.row.customer ? (
              <Typography
                variant="caption"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.5,
                  color: "text.secondary",
                  fontSize: "0.8rem",
                }}
              >
                <Person sx={{ fontSize: "0.875rem" }} color="disabled" />
                {params.row.customer?.first_name}
              </Typography>
            ) : !!params.row.location ? (
              <Typography
                variant="caption"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.5,
                  color: "text.secondary",
                  fontSize: "0.8rem",
                }}
              >
                <Place sx={{ fontSize: "0.875rem" }} color="disabled" />
                {params.row.location.name}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ mt: 0.5, color: "text.secondary" }}>
                ?
              </Typography>
            )}
          </Box>
        ),
        disableExport: true,
      },
      {
        field: "is_private",
        headerName: privateLabel,
        minWidth: isMobile ? 80 : undefined,
        width: isMobile ? 80 : undefined,
      },
      {
        field: "start_date",
        headerName: startAtLabel,
        headerAlign: "center",
        minWidth: isMobile ? 140 : 220,
        width: isMobile ? 140 : undefined,
        sortingOrder: ["desc", "asc", null],
        sortable: true,
        filterable: true,
        align: "left",
        valueGetter: (params: GridValueGetterParams) =>
          params?.row?.start_date
            ? `${formatWithTimezone(params.row.start_date, params.row.timezone)}`
            : "-",
        renderCell: (params: GridRenderCellParams) => {
          if (!params?.row?.start_date) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography variant="body2">
              {formatWithTimezone(params.row.start_date, params.row.timezone)}
            </Typography>
          );
        },
      },
      {
        field: "manager_id",
        headerName: managerLabel,
        headerAlign: "center",
        minWidth: isMobile ? 100 : 150,
        width: isMobile ? 100 : undefined,
        sortable: true,
        filterable: true,
        align: "center",
        valueGetter: (params: GridValueGetterParams) => {
          if (!params?.row?.manager) return "-";
          // Check for full_name first (as used in other components), then fall back to first_name + last_name
          if (params.row.manager.full_name) {
            return params.row.manager.full_name;
          }
          const firstName = params.row.manager.first_name || "";
          const lastName = params.row.manager.last_name || "";
          return `${firstName} ${lastName}`.trim() || "-";
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params?.row?.manager) {
            return <Typography variant="body2">-</Typography>;
          }
          // Check for full_name first (as used in other components), then fall back to first_name + last_name
          let managerName = "-";
          if (params.row.manager.full_name) {
            managerName = params.row.manager.full_name;
          } else {
            const firstName = params.row.manager.first_name || "";
            const lastName = params.row.manager.last_name || "";
            managerName = `${firstName} ${lastName}`.trim() || "-";
          }
          return <Typography variant="body2">{managerName}</Typography>;
        },
      }
    );

    if (status == ProjectStatus.PUBLISHED)
      col.push(
        {
          field: "bid",
          headerName: offersLabel,
          headerAlign: "center",
          minWidth: isMobile ? 80 : 120,
          width: isMobile ? 80 : undefined,
          sortable: false,
          filterable: false,
          align: "center",
          renderCell: (params: GridRenderCellParams) => {
            const bidCount =
              params.row.resources?.filter(
                (resource: any) =>
                  resource.status !== NegotiationStatus.INVITED &&
                  resource.status !== NegotiationStatus.CANCELED &&
                  resource.status !== NegotiationStatus.REJECTED &&
                  resource.status !== NegotiationStatus.HIRED
              ) || [];
            if (bidCount.length === 0) {
              return (
                <Typography
                  key={params.row.id}
                  variant="body2"
                  style={{ fontWeight: 500, fontSize: isMobile ? 14 : 18 }}
                >
                  -
                </Typography>
              );
            }
            return (
              <Box key={params.row.id}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => onBidClick(params.row)}
                  style={{ fontWeight: 500, fontSize: isMobile ? 14 : 18 }}
                >
                  {bidCount.length || "-"}
                </Link>
              </Box>
            );
          },
          disableExport: true,
        },
        {
          field: "invited",
          headerName: invitesLabel,
          headerAlign: "center",
          minWidth: isMobile ? 80 : 120,
          width: isMobile ? 80 : undefined,
          sortable: false,
          filterable: false,
          align: "center",
          renderCell: (params: GridRenderCellParams) => {
            const invitesCount =
              params.row.resources?.filter(
                (resource: any) =>
                  resource?.status === NegotiationStatus.INVITED ||
                  resource?.status === "invited"
              ) || [];
            if (invitesCount.length === 0) {
              return (
                <Typography
                  key={params.row.id}
                  variant="body2"
                  style={{ fontWeight: 500, fontSize: isMobile ? 14 : 18 }}
                >
                  -
                </Typography>
              );
            }
            return (
              <Box key={params.row.id}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => onBidClick(params.row)}
                  style={{ fontWeight: 500, fontSize: isMobile ? 14 : 18 }}
                >
                  {invitesCount.length || "-"}
                </Link>
              </Box>
            );
          },
        }
      );
    else if (
      status == ProjectStatus.SCHEDULED ||
      status == ProjectStatus.IN_PROGRESS
    ) {
      const talentStatuses = (project: Project) => {
        const jobList = project.resources?.filter((it) => !!it.hired_at) || [];
        return (
          <Stack key={project.id}>
            {jobList.map((it) => {
              const status = getHiredTalentStatus(project, it);
              if (
                project.status == ProjectStatus.SCHEDULED ||
                project.status == ProjectStatus.IN_PROGRESS
              )
                return (
                  <>
                    <Stack direction={"row"} alignItems="center">
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                        sx={{ height: 20, fontSize: 12 }}
                      />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {it.resource?.full_name} ($
                        {project.type == "fixed" ? it.price : it.rate})
                      </Typography>
                    </Stack>
                  </>
                );
              else
                return (
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {it.resource?.full_name || "n/a"} ($
                    {project.type == "fixed" ? it.price : it.rate})
                  </Typography>
                );
            })}
          </Stack>
        );
      };

      col.push(
        {
          field: "talents",
          headerName: "Independent Professional (Rate)",
          headerAlign: "center",
          minWidth: isMobile ? 180 : 250,
          flex: isMobile ? 1 : undefined,
          align: "center",
          sortable: false,
          filterable: false,
          renderCell: (params: GridRenderCellParams) => {
            return <div key={params.row.id}>{talentStatuses(params.row)}</div>;
          },
          disableExport: true,
        },
        {
          field: "talents2",
          headerName: "Independent Professional (Rate)",
          valueGetter: (params: GridValueGetterParams) => {
            if (!params?.row) return "-";
            const jobList =
              params.row.resources?.filter((it: any) => !!it.hired_at) || [];
            return jobList
              .map(
                (it: any) =>
                  `${it.resource?.full_name || "n/a"} ($${params.row.type == "fixed" ? it.price : it.rate
                  })`
              )
              .join(";");
          },
        },
        {
          field: "is_photo_required",
          headerName: "Photos Required",
        }
      );
    } else if (status == ProjectStatus.COMPLETED) {
      col.push(
        {
          field: "talents",
          headerName: "Independent Professional",
          headerAlign: "center",
          minWidth: isMobile ? 180 : 250,
          flex: isMobile ? 1 : undefined,
          align: "center",
          sortable: false,
          filterable: false,
          valueGetter: (params: GridValueGetterParams) => {
            if (!params?.row) return "-";
            const jobList =
              params.row.resources?.filter((it: any) => !!it.hired_at) || [];
            if (jobList.length === 0) return "-";
            return jobList
              .map((it: any) => `${it.resource?.full_name || "n/a"}`)
              .join(";");
          },
          renderCell: (params: GridRenderCellParams) => {
            if (!params?.row) {
              return <Typography variant="body2">-</Typography>;
            }
            const jobList =
              params.row.resources?.filter((it: any) => !!it.hired_at) || [];
            if (jobList.length === 0) {
              return <Typography variant="body2">-</Typography>;
            }
            const names = jobList
              .map((it: any) => it.resource?.full_name)
              .filter((name: string | null | undefined): name is string => !!name);
            if (names.length === 0) {
              return <Typography variant="body2">-</Typography>;
            }
            return (
              <Typography variant="body2">
                {names.join("; ")}
              </Typography>
            );
          },
        },
        {
          field: "is_photo_required",
          headerName: "Photos Required",
        }
      );
    } else if (status == ProjectStatus.CANCELED) {
      col.push({
        field: "canceled_reason",
        headerName: "Cancel Reason",
        headerAlign: "center",
        minWidth: 200,
        sortable: true,
        filterable: false,
        align: "center",
        valueGetter: (params: GridValueGetterParams) =>
          params?.row?.canceled_reason || "-",
        renderCell: (params: GridRenderCellParams) => {
          if (!params?.row?.canceled_reason) {
            return <Typography variant="body2">-</Typography>;
          }
          return (
            <Typography variant="body2">
              {params.row.canceled_reason}
            </Typography>
          );
        },
      });
    }

    //last one is for control
    col.push({
      field: "actions",
      headerName: "Details",
      headerAlign: "center",
      minWidth: isMobile ? 60 : 100,
      type: "actions",
      align: "center",
      width: isMobile ? 60 : 50,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={params.row.id}
          icon={<OpenInNew />}
          onClick={() => navigate(`/works/${params.row.id}`)}
          label="Open Detail"
        />,
      ],
      disableExport: true,
    });
    return col;
  }, [status, isMobile, onView, locationLabel, managerLabel]);

  const handleSearch = React.useCallback((query?: string) => {
    setSearchQuery(query);
  }, []);

  const handleCreate = React.useCallback(() => {
    navigate(`/works/create`);
  }, []);

  const handleShowMap = React.useCallback(() => {
    navigate(`/works/map`);
  }, []);

  const CustomToolbar = React.useMemo(
    () => () =>
    (
      <TableToolbar
        onSearch={handleSearch}
        onCreate={handleCreate}
        onShowMap={handleShowMap}
        onRefresh={refetch}
      />
    ),
    [handleSearch]
  );

  return (
    <Paper
      sx={{
        width: "100%",
        height: "80vh",
        borderRadius: "20px",
        overflow: "hidden",
        position: "relative",
        isolation: "isolate",
        background: isDark
          ? `linear-gradient(135deg, ${alpha("#ffffff", 0.15)} 0%, ${alpha("#ffffff", 0.08)} 50%, ${alpha("#12903C", 0.05)} 100%)`
          : `linear-gradient(135deg, ${alpha("#ffffff", 0.25)} 0%, ${alpha("#ffffff", 0.15)} 50%, ${alpha("#12903C", 0.08)} 100%)`,
        backdropFilter: "blur(40px) saturate(180%)",
        WebkitBackdropFilter: "blur(40px) saturate(180%)",
        border: `1.5px solid ${isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8)}`,
        boxShadow: isDark
          ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
          : "0 8px 32px rgba(18, 144, 60, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "20px",
          pointerEvents: "none",
          boxShadow: isDark
            ? "inset 0 1px 0 0 rgba(255, 255, 255, 0.3)"
            : "inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
          zIndex: 1,
        },
      }}
    >
      {/* Gradient overlays for glass shine effect */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "50%",
          background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
          pointerEvents: "none",
          zIndex: 1,
          borderRadius: "20px 20px 0 0",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, transparent 0%, ${alpha("#12903C", 0.03)} 100%)`,
          pointerEvents: "none",
          zIndex: 1,
          borderRadius: "20px",
        }}
      />
      <DataGridCompat
        rows={rows || []}
        columns={columns}
        paginationMode="server"
        rowCount={rowCount}
        page={page as any}
        onPageChange={(newPage: any) => setPage(newPage)}
        pageSize={pageSize as any}
        rowsPerPageOptions={[100]}
        onPageSizeChange={(newSize: any) => setPageSize(newSize)}
        sortModel={sortModel}
        sortingMode="server"
        onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
        loading={isLoading || isFetching}
        components={{
          LoadingOverlay: LinearProgress,
          NoResultsOverlay: NoResultsOverlay,
          Toolbar: CustomToolbar,
        }}
        disableSelectionOnClick
        disableColumnFilter
        // rowHeight={42}
        // density="compact"
        // @ts-ignore
        getRowHeight={() =>
          status == ProjectStatus.SCHEDULED || ProjectStatus.IN_PROGRESS
            ? 56
            : undefined
        }
        sx={{
          border: "none",
          width: "100%",
          position: "relative",
          zIndex: 2,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: (theme: Theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.5)",
            backdropFilter: "blur(15px) saturate(180%)",
            WebkitBackdropFilter: "blur(15px) saturate(180%)",
            borderBottom: (theme: Theme) =>
              `2px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(46, 125, 50, 0.3)"}`,
            borderRadius: "0",
            minHeight: isMobile ? "48px !important" : "56px !important",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: isMobile ? "0.7rem" : "0.875rem",
              textTransform: "uppercase",
              letterSpacing: isMobile ? "0.5px" : "0.8px",
              color: "#12903C",
            },
            "& .MuiDataGrid-columnHeader": {
              "&:focus": {
                outline: "none",
              },
              "&:focus-within": {
                outline: "none",
              },
            },
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            borderBottom: (theme: Theme) =>
              `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.06)"}`,
            "&:hover": {
              backgroundColor: (theme: Theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(46, 125, 50, 0.08)",
              transform: "translateX(6px)",
              boxShadow: (theme: Theme) =>
                theme.palette.mode === "dark"
                  ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 4px 12px rgba(0, 0, 0, 0.1)",
              "& .MuiDataGrid-cell": {
                borderBottom: (theme: Theme) =>
                  `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(46, 125, 50, 0.15)"}`,
              },
            },
            "&.Mui-selected": {
              backgroundColor: (theme: Theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(25, 118, 210, 0.15)"
                  : "rgba(25, 118, 210, 0.08)",
              "&:hover": {
                backgroundColor: (theme: Theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(25, 118, 210, 0.2)"
                    : "rgba(25, 118, 210, 0.12)",
              },
            },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: (theme: Theme) =>
              `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.06)"}`,
            "&:focus": {
              outline: "none",
            },
            "&:focus-within": {
              outline: "none",
            },
            fontSize: isMobile ? "0.75rem" : "0.875rem",
            padding: isMobile ? "8px 10px" : "12px 16px",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: (theme: Theme) =>
              `2px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
            backgroundColor: (theme: Theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(255, 255, 255, 0.5)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            minHeight: isMobile ? "56px" : "64px",
          },
          "& .MuiDataGrid-toolbarContainer": {
            padding: isMobile ? "12px" : "16px",
            backgroundColor: "transparent",
          },
        }}
      />
    </Paper>
  );
};
export default ProjectGrid;
